import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CourseProgressCard = ({ course, enrollment, nextLesson }) => {
  const progressPercentage = enrollment?.progressPercent || 0;
  const isComplete = progressPercentage >= 100;
  const continueUrl = `/video-learning-interface?orgId=${enrollment?.orgId}&courseId=${enrollment?.courseId}${
    nextLesson?.id ? `&lessonId=${nextLesson?.id}` : ''
  }`;

  const [animatedProgress, setAnimatedProgress] = useState(0);
  const cardRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Counts the bar up from 0 to the real progress every time this card
  // scrolls into view — mirrors the streak ring animation on the hero card.
  useEffect(() => {
    if (!cardRef.current) return;

    const target = progressPercentage;
    const runCountUpAnimation = () => {
      cancelAnimationFrame(animationFrameRef.current);
      const duration = 900;
      const startTime = performance.now();

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        setAnimatedProgress(target * eased);
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(step);
        }
      };

      setAnimatedProgress(0);
      animationFrameRef.current = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) runCountUpAnimation();
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(cardRef.current);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [progressPercentage]);

  return (
    <div
      ref={cardRef}
      className="bg-white/30 backdrop-blur-xl rounded-xl border border-white/40 overflow-hidden hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
    >
      <div className="h-28 bg-muted flex items-center justify-center relative">
        {course?.thumbnailUrl ? (
          <img src={course?.thumbnailUrl} alt={course?.title} className="w-full h-full object-cover" />
        ) : (
          <Icon name="BookOpen" size={24} className="text-muted-foreground" />
        )}
        {isComplete && (
          <span className="absolute top-2 right-2 text-xs font-medium px-2 py-0.5 rounded-full bg-success text-white flex items-center gap-1">
            <Icon name="Check" size={12} /> Complete
          </span>
        )}
      </div>

      <div className="p-4 space-y-3">
        <h4 className="font-semibold text-foreground text-sm leading-snug line-clamp-2">{course?.title}</h4>

        <div>
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
            <span>
              {enrollment?.lessonsCompleted || 0} of {enrollment?.lessonsTotal || 0} lessons
            </span>
            <span className="font-medium text-foreground">{Math.round(animatedProgress)}%</span>
          </div>
          <div className="w-full bg-white/40 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-accent h-1.5 rounded-full"
              style={{ width: `${animatedProgress}%` }}
            />
          </div>
        </div>

        {nextLesson ? (
          <Link to={continueUrl}>
            <Button variant="outline" size="sm" fullWidth iconName="Play" iconPosition="left">
              {progressPercentage > 0 ? 'Continue' : 'Start'}
            </Button>
          </Link>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-success font-medium pt-1">
            <Icon name="Award" size={14} /> Course completed
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseProgressCard;